import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AuthMedia from "../components/AuthMedia";
import { BookHeart, Lock, Gift, Sparkles, ArrowRight, ArrowLeft, Info, MessagesSquare, TreePine, ScrollText, ShieldCheck, Music4 } from "lucide-react";

const FEATURES = [
  { icon: BookHeart, title: "Il libro dei ricordi", text: "Foto, video, voci e lettere custoditi e svelati al momento giusto." },
  { icon: MessagesSquare, title: "Messaggi dedicati", text: "Parole pensate solo per te, che arrivano nel momento speciale scelto per te." },
  { icon: TreePine, title: "Albero di famiglia", text: "Le radici e le storie della famiglia in un albero interattivo da esplorare." },
  { icon: ScrollText, title: "Testamento", text: "Volontà e disposizioni custodite e consegnate a chi ne ha diritto." },
  { icon: Gift, title: "Cassetta dei doni", text: "Tutti i lasciti a te dedicati, anche da più persone care, in un unico luogo." },
  { icon: Music4, title: "Dediche musicali", text: "Una canzone abbinata a un ricordo, per riascoltarla e riviverla insieme." },
];

const HeirPortal = () => {
  const { token } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [portal, setPortal] = useState(undefined);
  const [phase, setPhase] = useState("intro");

  const load = () => api.get(`/heir/portal/${token}`).then((res) => setPortal(res.data)).catch(() => setPortal(null));
  useEffect(() => { load(); }, [token]);

  if (portal === undefined)
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Caricamento…</div>;

  if (portal === null)
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center bg-white rounded-3xl border border-border p-12 max-w-md" data-testid="heir-portal-invalid">
          <Lock className="w-10 h-10 text-muted-foreground mx-auto mb-4" />
          <h1 className="font-serif text-2xl mb-2">Link non valido</h1>
          <p className="text-muted-foreground">Questo link non è più valido o non esiste.</p>
        </div>
      </div>
    );

  const { heir, owner } = portal;
  const emailMatch = user && heir.email && user.email && heir.email.toLowerCase() === user.email.toLowerCase();
  const registerUrl = `/accedi?mode=register&next=/doni${heir.email ? `&email=${encodeURIComponent(heir.email)}` : ""}`;
  const cover = portal.cover_image;
  const coverIsMedia = cover && typeof cover === "object" && cover.storage_path;

  return (
    <div className="min-h-screen">
      <header className="border-b border-border bg-[#F9F6F0]/85 backdrop-blur-md">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center gap-2">
          <BookHeart className="w-6 h-6 text-primary" />
          <span className="font-serif text-xl">Eterna</span>
          <span className="ml-auto text-sm text-muted-foreground">Per {heir.name}</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 fade-in">
        {phase === "intro" ? (
          <div className="bg-white rounded-3xl border border-border overflow-hidden" data-testid="heir-intro">
            <div className="h-40 bg-gradient-to-br from-[#FBEDE7] via-[#F4EAF0] to-[#EAF3EE] flex items-center justify-center">
              <div className="w-20 h-20 rounded-full bg-white/70 backdrop-blur flex items-center justify-center shadow-sm">
                <Gift className="w-9 h-9 text-primary" />
              </div>
            </div>
            <div className="p-8 sm:p-10 text-center">
              <p className="text-sm text-primary mb-1">Ciao {heir.name}</p>
              <h1 className="font-serif text-3xl sm:text-4xl tracking-tight">C'è un dono che ti aspetta</h1>
              <p className="text-muted-foreground mt-4 leading-relaxed max-w-lg mx-auto">
                {owner.name} ha custodito qualcosa di prezioso, pensato con cura proprio per te. Prima di aprirlo, ecco cosa puoi fare.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
                <a href="/come-funziona" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-white border border-border h-12 px-7 text-sm hover:border-primary/40 transition-colors" data-testid="heir-discover-eterna-btn">
                  <Info className="w-4 h-4" /> Scopri cos'è Eterna
                </a>
                <button onClick={() => setPhase("anteprima")} className="inline-flex items-center gap-2 rounded-full bg-primary text-white h-12 px-7 text-sm hover:bg-[#c9694f] transition-colors" data-testid="heir-discover-gift-btn">
                  <Sparkles className="w-4 h-4" /> Scopri il dono a te lasciato
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div data-testid="heir-anteprima">
            <button onClick={() => setPhase("intro")} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[#2C2A29] mb-4 transition-colors" data-testid="heir-anteprima-back">
              <ArrowLeft className="w-4 h-4" /> Indietro
            </button>
            <div className="bg-white rounded-3xl border border-border overflow-hidden">
              {coverIsMedia ? (
                <AuthMedia item={cover} guardianToken={token} className="w-full h-56 object-cover" />
              ) : (
                <div className="h-48 bg-gradient-to-br from-[#FBEDE7] via-[#F4EAF0] to-[#EAF3EE] flex items-center justify-center">
                  <Gift className="w-12 h-12 text-primary/70" />
                </div>
              )}
              <div className="p-8 sm:p-10">
                <p className="text-sm text-primary mb-1">Un dono da {owner.name}</p>
                <h1 className="font-serif text-3xl tracking-tight mb-4">Per te, {heir.name}</h1>
                <p className="text-[#3d3a38] leading-relaxed whitespace-pre-wrap text-lg">
                  {heir.welcome_message
                    ? heir.welcome_message
                    : `${owner.name} ha custodito dei ricordi pensati per te, con affetto. Sono qui, ad aspettarti.`}
                </p>
              </div>
            </div>

            <div className="bg-[#F9F6F0] rounded-3xl border border-border p-8 sm:p-10 mt-6" data-testid="heir-features">
              <div className="text-center mb-8">
                <div className="w-11 h-11 rounded-xl bg-white border border-border flex items-center justify-center mx-auto mb-3"><Sparkles className="w-5 h-5 text-primary" /></div>
                <h2 className="font-serif text-2xl sm:text-3xl">Tutto ciò che {owner.name} ha creato con Eterna</h2>
                <p className="text-muted-foreground mt-2 max-w-lg mx-auto">Eterna è il luogo dove custodire e tramandare ciò che conta davvero. Ecco cosa potrai ricevere e, se vorrai, creare anche tu.</p>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {FEATURES.map((f) => (
                  <div key={f.title} className="bg-white rounded-2xl border border-border p-5">
                    <div className="w-10 h-10 rounded-xl bg-[#FBEDE7] flex items-center justify-center mb-3"><f.icon className="w-5 h-5 text-primary" /></div>
                    <h3 className="font-serif text-lg leading-tight">{f.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{f.text}</p>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-center gap-2 mt-8 text-sm text-muted-foreground">
                <ShieldCheck className="w-4 h-4 text-[#4e8f6f]" />
                <span>Sicuro e privato: ogni contenuto si apre solo secondo le regole scelte da {owner.name}.</span>
              </div>
            </div>

            <div className="bg-[#2C2A29] text-white rounded-3xl p-8 sm:p-10 mt-6 text-center" data-testid="heir-register-block">
              <Sparkles className="w-7 h-7 text-[#81B29A] mx-auto mb-3" />
              <h2 className="font-serif text-2xl">Questo è solo l'inizio</h2>
              <p className="text-white/75 mt-3 leading-relaxed max-w-lg mx-auto">
                Per aprire tutti i doni lasciati per te — e raccoglierli in un unico posto, anche quelli di altre persone care — crea il tuo accesso gratuito a Eterna.
              </p>
              {emailMatch ? (
                <button onClick={() => navigate("/doni")} className="inline-flex items-center gap-2 rounded-full bg-primary text-white h-12 px-8 text-sm hover:bg-[#c9694f] transition-colors mt-6" data-testid="heir-open-gifts-btn">
                  Apri la tua cassetta dei doni <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <>
                  <button onClick={() => navigate(registerUrl)} className="inline-flex items-center gap-2 rounded-full bg-primary text-white h-12 px-8 text-sm hover:bg-[#c9694f] transition-colors mt-6" data-testid="heir-register-cta-btn">
                    Registrati per vedere i doni <ArrowRight className="w-4 h-4" />
                  </button>
                  <p className="text-sm text-white/60 mt-4">
                    Hai già un account? <Link to="/accedi?next=/doni" className="text-white underline hover:text-white/80" data-testid="heir-login-link">Accedi</Link>
                  </p>
                </>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default HeirPortal;
